import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Inject } from "@angular/core";
import { Subject, Observable, Subscription } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { EvaluationScheduleFormState, EvaluationScheduleFormStateModel, InitBulkScheduleForm, InitEvaluationScheduleForm, SubmitEvaluationScheduleForm } from './evaluation-schedule-form.state';
import { FormGroup, FormControl, FormArray, Validators } from '@angular/forms';
import {Sample, Evaluation, SampleStorage, Status, CustomProtocolService, StorageRegime, StorageRegimeService} from '@core/data';
import { Library, Schedule, Protocol, SampleType } from '@library';
import { takeUntil } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { parseDuration, addDuration, coerseDateProperty } from '@core/utils';
import { Snackbar } from '@core/material';
import { evaluationsFromStorageRegime } from '../sample-form/storage-regime-schedule';
import { LIBRARY } from '@app/evaluation/library';


export interface ScheduleDefaults {
    sampleKey?: string,
    cropId: string,
    cropIds: string[],
    sampleCropMap: Record<string, string>,
    sampleType: SampleType,
    sampleCodes: string[],
    sampleKeys: string[],
    massAction: boolean,
}

export interface EvaluationScheduleFormDialogData {
    orgKey: string;
    sampleKey?: string;
    defaults?: ScheduleDefaults;
}

@Component({
    selector: 'pv-evaluation-schedule-form-dialog',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    templateUrl: 'evaluation-schedule-form.dialog.html'
})
export class EvaluationScheduleFormDialog implements OnInit, OnDestroy {

    @Select(EvaluationScheduleFormState)
    state$: Observable<EvaluationScheduleFormStateModel>;

    @Select(EvaluationScheduleFormState.sample)
    sample$: Observable<Sample>;

    protocolControl: FormControl = new FormControl(null, [Validators.required])

    formGroup = new FormGroup({
        sampleKey: new FormControl(null, [Validators.required]),
        scheduleId: new FormControl(null, [Validators.required]),
        cropSchedules: new FormArray([]),
        protocolId: this.protocolControl,
        startDate: new FormControl(null, [Validators.required]),
        duration: new FormControl(null, [Validators.required, Validators.min(1)]),
        size: new FormControl(null, [Validators.required, Validators.min(1)])
    });

    scheduleOptions: Schedule[] = [];
    scheduleOptionsByCrop: { cropId: string, cropLabel: string, schedules: Schedule[] }[] = [];
    isMassAction: boolean = false;
    isMultiCrop: boolean = false;

    resultingEvaluations: Partial<Evaluation>[] = [];
    resultingProtocol: Protocol = null;
    resultingSchedule: Schedule = null;

    // Prefix keeps a regime key from ever being read as a library schedule ID.
    readonly customRegimePrefix = 'regime:';
    customRegimeOptions: StorageRegime[] = [];
    private customRegimeRequest: Subscription;

    private _destroy$ = new Subject();

    constructor(
        @Inject(LIBRARY) private _library: Library,
        private _store: Store,
        @Inject(MAT_DIALOG_DATA) private _data: EvaluationScheduleFormDialogData,
        private _ref: MatDialogRef<EvaluationScheduleFormDialog>,
        private _snakcbar: Snackbar,
        private _custom_protocolService: CustomProtocolService,
        private _storageRegimeService: StorageRegimeService
    ){
        this.cropSchedulesArray.setValidators(() => this.allCropsHaveNoSchedules ? { allCropsHaveNoSchedules: true } : null);
    }

    ngOnInit(){

        if (this._data.defaults && this._data.defaults.massAction) {
            const { sampleKeys, cropId, sampleType, cropIds } = this._data.defaults;
            this.isMassAction = true;
            this._store.dispatch(new InitBulkScheduleForm(sampleKeys, this._data.orgKey, cropId, sampleType, cropIds));

        } else this._store.dispatch(new InitEvaluationScheduleForm(this._data.sampleKey, this._data.orgKey));

        this.state$.pipe(takeUntil(this._destroy$))
            .subscribe(state => {

                if(state.status === Status.COMPLETE){
                    this._ref.close(state.result);
                }else if(state.status === Status.LOADING){
                    this.formGroup.disable();
                }else{
                    this.formGroup.enable();
                }

            });

        this.sample$.pipe(takeUntil(this._destroy$))
            .subscribe(sample => {
                if(sample) this.reset(sample);
                else this.reset(null);
            });

        this.formGroup.valueChanges.pipe(takeUntil(this._destroy$))
            .subscribe(form => this.calcResultingEvaluations());

    }

    ngOnDestroy(){
        this._destroy$.next();
        this._destroy$.complete();
    }

    private get cropSchedulesArray(): FormArray {
        return this.formGroup.get('cropSchedules') as FormArray;
    }

    private reset(sample: Sample){
        let cropId = null;
        let sampleKey: string | string[] = sample?.key || null;

        this.isMultiCrop = this.isMassAction && (this._data.defaults?.cropIds?.length ?? 0) > 1;

        if (this.isMassAction) {
            cropId = this._data.defaults.cropId;
            sampleKey = this._data.defaults.sampleKeys;
        } else if (!this.isMassAction && sample?.scionCultivar) cropId = sample.scionCultivar.cropId;

        if(!this.isMultiCrop){
            this.scheduleOptions = cropId ? this._library.schedulesByCrop(cropId) : [];
        }else{
            this.scheduleOptions = [];
        }

        this.formGroup.reset({
            sampleKey: sampleKey,
            scheduleId: this.isMultiCrop ? null : (this.scheduleOptions.length ? this.scheduleOptions[0].id : null),
            startDate: sample?.birthDate || moment(),
            duration: 1,
            size: sample?.size || 1,
        });

        this.cropSchedulesArray.clear();
        this.loadCustomRegimeOptions(cropId);

        if(this.isMultiCrop){
            this.scheduleOptionsByCrop = this._data.defaults.cropIds
                .map(cropId => ({
                    cropId,
                    cropLabel: this._library.crops.has(cropId) ? this._library.crops.get(cropId).label : cropId,
                    schedules: this._library.crops.has(cropId) ? this._library.schedulesByCrop(cropId) : [],
                }))
                .sort((a, b) => a.cropLabel.localeCompare(b.cropLabel));

            this.scheduleOptionsByCrop.forEach(entry => {
                this.cropSchedulesArray.push(new FormGroup({
                    cropId: new FormControl(entry.cropId),
                    scheduleId: new FormControl(
                        entry.schedules.length ? entry.schedules[0].id : null,
                        entry.schedules.length ? [Validators.required] : []
                    ),
                }));
            });

            this.formGroup.get('scheduleId').clearValidators();
        }else{
            this.scheduleOptionsByCrop = [];
            this.formGroup.get('scheduleId').setValidators([Validators.required]);
        }

        this.formGroup.get('scheduleId').updateValueAndValidity({ emitEvent: false });
        this.cropSchedulesArray.updateValueAndValidity({ emitEvent: false });
    }

    // All bulk-selected samples (single-crop path) share one schedule/protocol/start-date/size,
    // so evaluationsForSample() produces identical rows per sample — collapse for the preview table.
    get dedupedResultingEvaluations(): Partial<Evaluation>[] {
        if (!this.isMassAction) return this.resultingEvaluations;

        return this.dedupeEvaluations(this.resultingEvaluations);
    }

    get resultingEvaluationsByCrop(): { cropId: string, cropLabel: string, evaluations: Partial<Evaluation>[], schedule: Schedule }[] {
        if (!this.isMultiCrop) return [];

        const sampleCropMap = this._data.defaults?.sampleCropMap || {};
        const cropSchedules: { cropId: string, scheduleId: string }[] = this.cropSchedulesArray.value;

        return this.scheduleOptionsByCrop
            .map(entry => {
                const scheduleId = cropSchedules.find(c => c.cropId === entry.cropId)?.scheduleId;

                return {
                    cropId: entry.cropId,
                    cropLabel: entry.cropLabel,
                    evaluations: this.dedupeEvaluations(
                        this.resultingEvaluations.filter(evalu => sampleCropMap[evalu.sampleKey] === entry.cropId)
                    ),
                    schedule: scheduleId ? this._library.schedules.get(scheduleId) : null,
                };
            })
            .filter(group => group.evaluations.length > 0);
    }

    // Same crop's samples share one schedule/protocol/start-date/size, so evaluationsForSample()
    // produces identical rows per sample — collapse to the unique set for a per-crop preview table.
    private dedupeEvaluations(evaluations: Partial<Evaluation>[]): Partial<Evaluation>[] {
        const seen = new Set<string>();
        const unique: Partial<Evaluation>[] = [];

        evaluations.forEach(evalu => {
            const { sampleKey, ...rest } = evalu;
            const key = JSON.stringify(rest);

            if (!seen.has(key)) {
                seen.add(key);
                unique.push(evalu);
            }
        });

        return unique;
    }

    get allCropsHaveNoSchedules(): boolean {
        return this.isMultiCrop
            && this.scheduleOptionsByCrop.length > 0
            && this.scheduleOptionsByCrop.every(entry => entry.schedules.length === 0);
    }

    get allCropsHaveNoSchedulesMessage(): string {
        const cropLabels = this.scheduleOptionsByCrop.map(entry => entry.cropLabel);
        return `No storage schedules are defined for any of the selected crops (${cropLabels.join(', ')}) — nothing to schedule.`;
    }

    private cropHasNoSchedules(cropId: string): boolean {
        const entry = this.scheduleOptionsByCrop.find(e => e.cropId === cropId);
        return !!entry && entry.schedules.length === 0;
    }

    cancel(){
        this._ref.close();
    }

    attempt(){
        if(!this.formGroup.valid){
            this._snakcbar.formInvalid();
            return;
        }

        if (this._data.defaults && this._data.defaults.massAction) {
            let evaluations: Partial<Evaluation>[] = [];

            this.formGroup.value.sampleKey.forEach(key => {
                if (this.isMultiCrop) {
                    const cropId = this._data.defaults?.sampleCropMap ? this._data.defaults.sampleCropMap[key] : null;

                    if (this.cropHasNoSchedules(cropId)) return; // expected exclusion, already surfaced via the picker's inline notice
                }

                const scheduleId = this.resolveScheduleId(key);

                if (!scheduleId) {
                    this._snakcbar.error(`No schedule resolved for sample ${key} — it was skipped.`);
                    return;
                }

                this.calcResultingEvaluations(key, scheduleId);

                evaluations.push(...this.resultingEvaluations);
            });

            if (!evaluations || evaluations.length == 0) return this._snakcbar.error('Error while scheduling sample evaluations');

            return this._store.dispatch(new SubmitEvaluationScheduleForm(evaluations, this.regimesBySampleKey, this.formGroup.value.startDate));

        }

        if(!this.checkResultingEvaluations()) return;
        this._store.dispatch(new SubmitEvaluationScheduleForm(this.resultingEvaluations, this.regimesBySampleKey, this.formGroup.value.startDate));
    }

    private checkResultingEvaluations(): boolean {
        if(!Array.isArray(this.resultingEvaluations) || !this.resultingEvaluations.length){
            this._snakcbar.error("Chosen options does not result in any scheduled evaluation. Check your input.");
            return false;
        }

        return true;
    }

    private resolveScheduleId(sampleKey: string): string {
        if(!this.isMultiCrop) return this.formGroup.value.scheduleId;

        const cropId = this._data.defaults?.sampleCropMap ? this._data.defaults.sampleCropMap[sampleKey] : null;
        const entry: { cropId: string, scheduleId: string } = this.cropSchedulesArray.value.find(
            (c: { cropId: string, scheduleId: string }) => c.cropId === cropId
        );

        return entry ? entry.scheduleId : null;
    }

    // Populated per-sample by evaluationsForCustomRegime()/evaluationsForSample() as each
    // sample's schedule is resolved — only Custom Regimes populate an entry, a Legacy Library
    // schedule has no action templates to resolve. Used at submit time to generate this
    // submission's Scheduled/Sample-level Actions alongside its Evaluations.
    private regimesBySampleKey: Record<string, StorageRegime> = {};

    private loadCustomRegimeOptions(cropId: string | null): void {
        this.customRegimeOptions = [];
        if (this.isMultiCrop || !cropId) return;

        // Cancels any earlier request, so a slow response for a previous crop cannot overwrite this one.
        this.customRegimeRequest?.unsubscribe();

        this.customRegimeRequest = this._storageRegimeService.list(this._data.orgKey)
            .pipe(takeUntil(this._destroy$))
            .subscribe(regimes => {
                this.customRegimeOptions = regimes.filter(regime => !regime.isGlobal && (regime.cropIds || []).includes(cropId));
            });
    }

    isCustomRegimeId(scheduleId: string): boolean {
        return !!scheduleId && scheduleId.startsWith(this.customRegimePrefix);
    }

    private evaluationsForCustomRegime(sampleKey: string, scheduleId: string): Partial<Evaluation>[] {
        const regime = this.customRegimeOptions.find(r => r.key === scheduleId.slice(this.customRegimePrefix.length));

        if (!regime) {
            delete this.regimesBySampleKey[sampleKey];
            return [];
        }

        this.regimesBySampleKey[sampleKey] = regime;

        const form = this.formGroup.value;
        this.resultingSchedule = null;

        return evaluationsFromStorageRegime(sampleKey, regime, form.protocolId, moment(form.startDate), form.size);
    }

    private evaluationsForSample(sampleKey: string, scheduleId: string): Partial<Evaluation>[] {
        if (this.isCustomRegimeId(scheduleId)) return this.evaluationsForCustomRegime(sampleKey, scheduleId);

        delete this.regimesBySampleKey[sampleKey];

        const form = this.formGroup.value;
        const schedule = this.resultingSchedule = this._library.schedules.get(scheduleId);

        if(!schedule) return [];

        const evaluations: Partial<Evaluation>[] = [];

        schedule.evaluations.forEach(regime => {

            let start = moment(form.startDate);

            regime.storage.forEach(store => {
                let dur = parseDuration(store.duration);
                start = addDuration(start, dur);
            });

            evaluations.push({
                label: regime.label,
                evalStartDate: coerseDateProperty(start),
                evalEndDate: coerseDateProperty(start.clone().add(form.duration || 1, 'days')),
                protocolId: form.protocolId,
                scheduleId: schedule.id,
                size: form.size,
                storageRegime: regime.storage,
                sampleKey: sampleKey
            });

        });

        return evaluations;
    }

    private calcResultingEvaluations(overrideKey?: string, overrideScheduleId?: string){

        const form = this.formGroup.value;

        this.resultingEvaluations = [];
        this.resultingProtocol = this._library.protocols.get(form.protocolId);

        if(!this.formGroup.valid){
            return;
        }

        if(overrideKey !== undefined){
            const scheduleId = overrideScheduleId !== undefined ? overrideScheduleId : this.resolveScheduleId(overrideKey);
            this.resultingEvaluations = this.evaluationsForSample(overrideKey, scheduleId);
            return;
        }

        const sampleKey = form.sampleKey;

        if(Array.isArray(sampleKey)){
            this.resultingEvaluations = sampleKey.reduce(
                (all: Partial<Evaluation>[], key: string) => all.concat(this.evaluationsForSample(key, this.resolveScheduleId(key))),
                []
            );
        }else{
            this.resultingEvaluations = this.evaluationsForSample(sampleKey, this.resolveScheduleId(sampleKey));
        }

    }

}
